/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 * Tools object to place utility type items that are used in numerous places
 */

'use strict';

import { PropertyDataType } from "./dvtypes";
import {
    NUMBER_DECIMAL_CHARACTER,
    isDate,
    language
} from "./tools";
import { Dictionary } from "./Dictionary";
import $ from "jquery";

let formatDict = new Dictionary();

/**
 * Attempt to apply the specified format to the specified value and return a string that represents the value in the format.
 * @param {String} format - The format to use on the value
 * @param {*} value - The value to format
 * @returns {String}
 * @constructor
 */
export function NETtoJQueryFormatter (format, value) {
    let formatNET = String(format).toLowerCase(),
        check = formatDict.getVal(formatNET);

    //The jquery formatter does not properly format percentages since the % is not actually passed in ...BC
    if (check != null && !(formatNET.indexOf("p") > -1)) {
        return check[0] + String($.format.number(Number(value), check[1])) + check[2];
    }

    else {
        let formatJquery,
            finalFormat;

        // Currency
        if (formatNET.indexOf("c") > -1) {
            let indexChar = formatNET.indexOf("c");
            if ((indexChar + 2) <= formatNET.length && Number.isNumeric(formatNET.substring(indexChar + 1, indexChar + 2))) {
                let tempFormat = "";
                let numDecimals = formatNET.substring(indexChar + 1, indexChar + 2);
                for (let i = 0; i < numDecimals; i++) {
                    tempFormat += "0";
                }
                formatJquery = "#,###." + tempFormat;
            }

            else {
                formatJquery = "#,###.00";
            }

            finalFormat = ["$", formatJquery, ""];
        }

        // Decimal
        else if (formatNET.indexOf("d") > -1) {
            let indexChar = formatNET.indexOf("d");
            if ((indexChar + 2) <= formatNET.length && Number.isNumeric(formatNET.substring(indexChar + 1, indexChar + 2))) {
                let tempFormat = "";
                let numDecimals = formatNET.substring(indexChar + 1, indexChar + 2);
                for (let i = 0; i < numDecimals; i++) {
                    tempFormat += "0";
                }
                formatJquery = "0." + tempFormat;
            }

            else {
                formatJquery = "0";
            }

            finalFormat = ["", formatJquery, ""];
        }

        // Percent
        else if (formatNET.indexOf("p") > -1) {
            value *= 100;
            let indexChar = formatNET.indexOf("p");
            if ((indexChar + 2) <= formatNET.length && Number.isNumeric(formatNET.substring(indexChar + 1, indexChar + 2))) {
                let tempFormat = "";
                let numDecimals = formatNET.substring(indexChar + 1, indexChar + 2);
                for (let i = 0; i < numDecimals; i++) {
                    tempFormat += "0";
                }
                formatJquery = "0." + tempFormat;
            }

            else {
                formatJquery = "0.00";
            }
            finalFormat = ["", formatJquery, "%"];
        }

        else if (formatNET.indexOf("n") > -1) {
            let indexChar = formatNET.indexOf("n");
            if ((indexChar + 2) <= formatNET.length && Number.isNumeric(formatNET.substring(indexChar + 1, indexChar + 2))) {
                let tempFormat = "";
                let numDecimals = formatNET.substring(indexChar + 1, indexChar + 2);
                for (let i = 0; i < numDecimals; i++) {
                    tempFormat += "0";
                }
                formatJquery = "#,###." + tempFormat;
            }

            else {
                formatJquery = "#,###.00";
            }
            finalFormat = ["", formatJquery, ""];
        }

        // Currently unsupported or invalid display format
        else {
            if (formatNET === "" || formatNET.indexOf(":") < 0) {        //Either this is blank format, or not properly formatted...EK
                finalFormat = ["", "", ""];
            }
            else {
                formatJquery = formatNET.split(':')[1].split('}')[0];
                finalFormat = ["", formatJquery, ""];
            }
        }

        formatDict.add(formatNET, finalFormat);
        let retVal;
        try {
            retVal = finalFormat[0] + String($.format.number(Number(value), finalFormat[1])) + finalFormat[2];
        }
        catch (e) {
            retVal = String($.format.number(Number(value), "0"));
        }
        return retVal;
    }
}

/**
 *Formats the date time elements of a grid.
 *On the first run of the method, the standard Microsoft.NET date and time format strings are added to the dictionary.
 *Any key/value pairs not found in the dictionary will then be added.
 *A null date time will be defaulted to "M/d/yyyy h:mm:ss tt".
 */
let formatDictDate = new Dictionary();
formatDictDate.add("t", "h:mm tt");
formatDictDate.add("T", "h:mm:ss tt");
formatDictDate.add("d", "M/d/yyyy");
formatDictDate.add("D", "dddd, MMMM dd, yyyy");
formatDictDate.add("f", "dddd, MMMM dd, yyyy h:mm tt");
formatDictDate.add("F", "dddd, MMMM dd, yyyy h:mm:ss tt");
formatDictDate.add("g", "M/d/yyyy h:mm tt");
formatDictDate.add("G", "M/d/yyyy h:mm:ss tt");
formatDictDate.add("m", "MMMM d");
formatDictDate.add("y", "MMMM, yyyy");
formatDictDate.add("r", "ddd, dd MMM yyyy HH:mm:ss");
formatDictDate.add("s", "yyyy-MM-ddTHH:mm:ss");
formatDictDate.add("u", "yyyy-MM-dd HH:mm:ssZ");

/**
 * Return a NET to JQuery format string
 * @param {String} format
 * @returns {String}
 */
export function NETtoJQueryFormatterDate (format) {
    format = format.replace("{0:", "");
    format = format.replace("}", "");

    let check = formatDictDate.getVal(format);
    if (check != null) {
        return check;
    }

    let newFormat;
    switch (format) {
        case "":
        case null:
            newFormat = "M/d/yyyy h:mm:ss tt";
            break;
        default:
            newFormat = format;
    }
    formatDictDate.add(format, newFormat);

    return newFormat;
}

let numberFormats = {
    baseNumber: {
        regex: /{0?:n\d?}/i,
        digits: /\d(?=})/i
    },
    currencyNumber: {
        regex: /{0?:c\d?}/i,
        digits: /\d(?=})/i
    },
    decimalNumber: {
        regex: /{0?:d\d?}/i,
        digits: /\d(?=})/i
    },
    percentageNumber: {
        regex: /{0?:p\d?}/i,
        digits: /\d(?=})/i
    }
};

let dateFormats = {
    time_t: {
        regex: /{0:t}/
    },
    time_T: {
        regex: /{0:T}/
    },
    date_d: {
        regex: /{0:d}/
    },
    date_D: {
        regex: /{0:D}/
    },
    date_f: {
        regex: /{0:f}/
    },
    date_F: {
        regex: /{0:F}/
    },
    date_g: {
        regex: /{0:g}/
    },
    date_G: {
        regex: /{0:G}/
    },
    date_mM: {
        regex: /{0:[mM]}/
    },
    date_yY: {
        regex: /{0:[yY]}/
    },
    date_rR: {
        regex: /{0:[rR]}/
    },
    date_s: {
        regex: /{0:s}/
    },
    date_u: {
        regex: /{0:u}/
    },
    date_ddd_MMM_d_yyy: {
        regex: /^({0:)?ddd,\sMMM\sd,\syyyy}?$/
    },
    date_dddd_MMMM_d_yyyy: {
        regex: /^({0:)?dddd,\sMMMM\sd,\syyyy}?$/
    },
    date_M_d_yyyy: {
        regex: /^({0:)?M\/d\/yyyy}?$/
    },
    date_MM_dd_yy: {
        regex: /^({0:)?MM\/dd\/yy}?$/
    },
    date_MM_dd_yyyy: {
        regex: /^({0:)?MM\/dd\/yyyy}?$/
    }
};

/**
 * Attempt to format the specified value using the formatting information in this instance
 * @param {ColumnTextFormatter} formatter - the formatter to use to format text
 * @param {string|number|Date} value - the value to format
 * @returns {string}
 */
function formatToText (formatter, value) {
    if (formatter._recreate || formatter._formatter._recreate) {
        // we need to try and determine what type of formatter to use based on this value.

        /**
         * Attempt to create a new Column Text Formatter
         * @type {null|ColumnTextFormatter|FormatterObj}
         */
        let test = createTextFormatterObj(value, formatter._formatter._formatString, { TextFormatterObj: null});
        if (test instanceof ColumnTextFormatter) {
            // we got a "real" formatter; use it's values to update this one!
            formatter._formatter = test._formatter;
            formatter._recreate = formatter._formatter == null;

            if (test._formatter instanceof Intl.DateTimeFormat) {
                formatter._type = FormatType.DATE_FORMAT;
            }
            else if (test._formatter instanceof Intl.NumberFormat) {
                formatter._type = FormatType.NUMBER_FORMAT;
            }
        }
    }

    let formatted = String(value);

    if (formatter._formatter) {
        if (formatter.type === FormatType.DATE_FORMAT) {
            value = (!isDate(value)) ? new Date(value) : value;
            if (value.toString() === "Invalid Date") {
                return "NaN";
            }
        }

        //Format and cleanup the value
        formatted = formatter._cleanup(formatter._formatter.format(value));
    }

    return formatted;
}

const FormatType = {
    NUMBER_FORMAT: 1,
    DATE_FORMAT: 2
};

/**
 * Default cleanup function that simply returns what was passed in.
 * @param {*} value - The value that was passed in
 * @returns {*} - Returns the same value as was passed in
 */
function defaultCleanup (value) {
    return value;
}

function returnValueString (value) {
    return String(value);
}

class FormatterObj {
    /**
     * Create a formatter shell object to encapsulate the requirement to create a new formatter if there was no data type when originally created.
     * @param {Function} formatFunction - The function to execute for formatting
     * @param {string} [formatString = ''] - The format string for this formatter.
     * @constructor
     */
    constructor (formatFunction, formatString = "") {
        /**
         * Set the internal function to use for this formatter
         * @type {Function}
         * @protected
         */
        this._format = formatFunction || returnValueString;

        /**
         * The format string to use
         * @type {string}
         * @protected
         */
        this._formatString = formatString;

        /**
         * Flag to indicate if this formatter isn't really valid and should be recreated when attempting to format text
         * @type {boolean}
         * @protected
         */
        this._recreate = formatFunction == null;
    }

    format (value) {
        return this._format.call(this, value);
    }

}

/**
 * Encapsulate date time, number, and custom formatters in a single interface.
 * @class
 */
export class ColumnTextFormatter {
    /**
     * Create a text formatter for a column.
     * @param {FormatType|null} [type = null] - 1, Number formatter; 2, Date formatter
     * @param {Object|} [options = null] - Object to hold options for the Intl.DateTimeFormat object
     * @param {Function|null} [cleanup = null] - (OPTIONAL) Function to cleanup the resultant format from teh formatter object
     * @param {null|Intl.NumberFormat|Intl.DateTimeFormat|FormatterObj} [formatter = null] - (OPTIONAL) Object to override the formatter object with a custom one.
     * @constructor
     */
    constructor (type = null, options = null, cleanup = null, formatter = null) {
        /**
         * Formatter object to use for formatting
         * @type {null|Intl.NumberFormat|Intl.DateTimeFormat|FormatterObj}
         * @protected
         */
        this._formatter = formatter;

        /**
         * Retain the type of format object this is for
         * @type {FormatType}
         * @protected
         */
        this._type = type;

        /**
         * Flag to indicate if we should attempt to re-create the formatter (we were unable to create one last time)
         * @type {boolean}
         * @protected
         */
        this._recreate = formatter == null;

        /**
         * Optional cleanup function for the formatted text
         * @type {Function|Null}
         * @protected
         */
        this._cleanup = cleanup || defaultCleanup;

        // if we don't have a formatter object, attempt to create one based on the type specified
        if (this._formatter == null) {
            switch (this._type) {
                case ColumnTextFormatter.NumberFormat:
                    this._formatter = new Intl.NumberFormat([ (options && options.lang) || language.split('-')[0] ], options);
                    this._recreate = false;
                    break;

                case ColumnTextFormatter.DateTimeFormat:
                    this._formatter = new Intl.DateTimeFormat([ (options && options.lang) || language.split('-')[0] ], options);
                    this._recreate = false;
                    break;

                default:
                    // we don't know what kind of formatter to use; we will try to create it when we format data
            }
        }
    }

    /**
     * Attempt to format the specified value.
     * @param {*} value - value to format
     * @returns {string} - return the specified string or the formatted string if formatting was successful.
     */
    format (value) {
        return formatToText(this, value);
    }

    /**
     * Return the type of formatter this is
     * @returns {FormatType}
     */
    get type () {
        return this._type;
    }

    /**
     * Return the value for creating a number formatter
     * @returns {FormatType}
     * @constructor
     */
    static get NumberFormat () {
        return FormatType.NUMBER_FORMAT;
    }

    /**
     * Return the value for creating a date time formatter
     * @returns {FormatType}
     * @constructor
     */
    static get DateTimeFormat () {
        return FormatType.DATE_FORMAT;
    }
}

/**
 * Create a text formatter object based on the specified data type or value.
 * @param {*} value - The value to format; used to attempt to identify data types if not provided
 * @param {string} formatString - String to use for formatting
 * @param {zebra.ui.Layoutable|zebra.ui.Panel|{ TextFormatterObj: ColumnTextFormatter|null}} [controlObj = {}] - The object control to apply the formatter to
 * @param {PropertyDataType} [dataType = null] - (OPTIONAL) DataType of the values to format
 * @returns {FormatterObj}
 */
export function createTextFormatterObj(value, formatString, controlObj = { TextFormatterObj: null}, dataType = null) {
    let co = 'en-US';

    // if we don't have a data type, we need to calculate the data type from the value...WR
    if (dataType == null && value != null && value !== "") {
        //if there is no dataType provided or the type is a string (for labels) parse the value to determine what format to use...MD
        try {
            //parse for integer type
            if (Number.isNumeric(value)) {
                if (typeof value === "string") {
                    if (value.indexOf(NUMBER_DECIMAL_CHARACTER) === -1) {
                        dataType = PropertyDataType.Integer;
                    }
                    else {
                        dataType = PropertyDataType.Decimal;
                    }
                }
                else {
                    let testNumber = Number(value);
                    if (Math.floor(testNumber) === testNumber) {
                        dataType = PropertyDataType.Integer;
                    }
                    else {
                        dataType = PropertyDataType.Decimal;
                    }
                }
            }
        } catch (e) {
            //catch the error and try the next dataType
        }

        //if not an integer try to validate as a date. If that does fail format as a string...MD
        if (dataType == null) {
            try {
                /**
                 * Attempt to create a date object from the value
                 * @type {Date}
                 */
                let test = new Date(value),
                    /**
                     * Get a number from the date value
                     * @type {number}
                     */
                    subTest = 0;

                if (test.toString() === "Invalid Date") {
                    //If the original try is invalid try again by parsing the value first...BC
                    subTest = Date.parse(value);
                    if (subTest != null) {
                        //it is a date format
                        test = new Date(subTest);
                        if (test.toString !== "Invalid Date") {
                            dataType = PropertyDataType.DateTime;
                        }
                    }
                }
                else {
                    dataType = PropertyDataType.DateTime;
                }

            } catch (e) {
                //If the parsed value is null, it is an invalid date
            }
        }
    }

    /**
     * Attempt to determine if the format is a number format, and if so apply a formatter object for the control.
     * @param {string} format - The format to use for the text displayed by the control
     * @param {zebra.ui.Layoutable|zebra.ui.Panel|{TextFormatterObj: ColumnTextFormatter|null}} control - The control to add a format object to
     * @returns {boolean} - TRUE, applied a format object; FALSE, did not apply a format object
     */
    function attemptNumberFormats (format, control) {
        let parseDigits;

        if (numberFormats.currencyNumber.regex.test(format)) {
            //currency format, examples {0:C2} $4.00, {0:C4} $4.0000

            parseDigits = numberFormats.currencyNumber.digits.exec(format);
            parseDigits = (parseDigits != null) ? parseInt(parseDigits) : 2;

            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.NumberFormat, {
                lang: co,
                style: 'currency',
                currency: 'USD',
                useGrouping: true,
                minimumFractionDigits: parseDigits,
                maximumFractionDigits: parseDigits
            });
        }
        else if (numberFormats.decimalNumber.regex.test(format)) {
            //decimal format, examples {0:D2} 4.00, {0:D4} 4.0000

            parseDigits = numberFormats.decimalNumber.digits.exec(format);
            parseDigits = (parseDigits != null) ? parseInt(parseDigits) : 1;

            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.NumberFormat, {
                lang: language,
                style: 'decimal',
                useGrouping: false,
                minimumFractionDigits: parseDigits,
                maximumFractionDigits: parseDigits,
                minimumIntegerDigits: 1
            });
        }
        else if (numberFormats.baseNumber.regex.test(format)) {
            //numeric format

            parseDigits = numberFormats.baseNumber.digits.exec(format);
            parseDigits = (parseDigits != null) ? parseInt(parseDigits) : 0;

            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.NumberFormat, {
                lang: language,
                style: 'decimal',
                useGrouping: true,
                minimumFractionDigits: parseDigits,
                maximumFractionDigits: parseDigits,
                minimumIntegerDigits: 1
            });
        }
        else if (numberFormats.percentageNumber.regex.test(format)) {
            //percentage format, examples {0:P} = 0.5% {0:P2} is 0.500%

            parseDigits = numberFormats.percentageNumber.digits.exec(format);
            parseDigits = (parseDigits != null) ? parseInt(parseDigits) : 0;

            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.NumberFormat, {
                lang: language,
                style: 'percent',
                useGrouping: false,
                minimumFractionDigits: parseDigits,
                maximumFractionDigits: parseDigits,
                minimumIntegerDigits: 1
            });
        }
        return control.TextFormatterObj != null;
    }

    /**
     * Attempt to determine if the format is a date/time format, and if so apply a formatter object for the control.
     * @param {string} format - The format to use for the text displayed by the control
     * @param {zebra.ui.Layoutable|zebra.ui.Panel|{TextFormatterObj: ColumnTextFormatter|null}} control - The control to add a format object to
     * @returns {boolean} - TRUE, applied a format object; FALSE, did not apply a format object
     */
    function attemptDateFormats (format, control) {
        /*
         * NOTE: There is no guarantee that the DateTimeFormat object will return these MS-based formats
         * as we want them to be (order, etc.). If we need to, we can create a new object, with the
         * DateTimeFormat object internal to it, have it create a string for a internal format, and then
         * have a "format" method that will format the passed date using the DateTimeFormat object, and then
         * parse the string to get the parts we want for the MS format and return that string.
         * NOTE's NOTE: MS formats are ALWAYS "en-US"...
         */
        if (dateFormats.time_t.regex.test(format)) {
            // "h:mm tt"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            return true;
        }
        else if (dateFormats.time_T.regex.test(format)) {
            // "h:mm:ss tt"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        }
        else if (dateFormats.date_d.regex.test(format)) {
            // "M/d/yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: 'numeric',
                day: 'numeric',
                year: 'numeric'
            });
        }
        else if (dateFormats.date_D.regex.test(format)) {
            // "dddd, MMMM dd, yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                weekday: 'long',
                month: 'long',
                day: '2-digit',
                year: 'numeric'
            });
        }
        else if (dateFormats.date_f.regex.test(format)) {
            //"dddd, MMMM dd, yyyy h:mm tt"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                weekday: 'long',
                month: 'long',
                day: '2-digit',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }
        else if (dateFormats.date_F.regex.test(format)) {
            // "dddd, MMMM dd, yyyy h:mm:ss tt"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                weekday: 'long',
                month: 'long',
                day: '2-digit',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        }
        else if (dateFormats.date_g.regex.test(format)) {
            // "M/d/yyyy h:mm tt"

            //Cannot use the DateTimeFormat here because it adds an extraneous comma...KB
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            }, (formatted) => {
                //Remove extra comma here and return...KB
                return formatted.replace(/,/g, "");
            });
        }
        else if (dateFormats.date_G.regex.test(format)) {
            // "M/d/yyyy h:mm:ss tt"

            //Cannot use the DateTimeFormat here because it adds an extraneous comma...KB
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            }, (formatted) => {
                //Remove extra comma here and return...KB
                return formatted.replace(/,/g, "");
            });
        }
        else if (dateFormats.date_mM.regex.test(format)) {
            // "MMMM d"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: 'long',
                day: 'numeric'
            });
        }
        else if (dateFormats.date_yY.regex.test(format)) {
            // "MMMM, yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: 'long',
                year: 'numeric'
            }, (formatted) => {
                return formatted.replace(" ", ", ");
            });
        }
        else if (dateFormats.date_rR.regex.test(format)) {
            // "ddd, dd MMM yyyy HH:mm:ss"

            //NOTE: toUTCString returns the RFC 1123 pattern date, just use this...KB
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, null, null,
                new FormatterObj(
                    /**
                     * Format the specified date as a UTC date string
                     * @param {Date} date
                     * @returns {*}
                     */
                    function (date) {
                        date = (!isDate(date)) ? new Date(date) : date;
                        if (date.toString() === 'Invalid Date') {
                            return 'NaN';
                        }
                        return date.toUTCString();
                    }
                ));
        }
        else if (dateFormats.date_s.regex.test(format)) {
            // "yyyy-MM-ddTHH:mm:ss"
            let formatter = new FormatterObj(
                /**
                 * Format the date as "yyyy-MM-ddTHH:mm:ss"
                 * @param {Date} date
                 * @returns {string}
                 */
                function (date) {
                    function pad (n) {
                        return n < 10 ? '0' + n : n;
                    }

                    date = (!isDate(date)) ? new Date(date) : date;
                    if (date.toString() === 'Invalid Date') {
                        return 'NaN';
                    }
                    return date.getFullYear() +
                        '-' + pad(date.getMonth() + 1) +
                        '-' + pad(date.getDate()) +
                        'T' + pad(date.getHours()) +
                        ':' + pad(date.getMinutes()) +
                        ':' + pad(date.getSeconds());
                });

            control.TextFormatterObj = new ColumnTextFormatter(
                ColumnTextFormatter.DateTimeFormat, null, null, formatter);
        }
        else if (dateFormats.date_u.regex.test(format)) {
            // "yyyy-MM-dd HH:mm:ssZ"
            let formatter = new FormatterObj(
                /**
                 * Format the date as "yyyy-MM-dd HH:mm:ssZ"
                 * @param {Date} date
                 * @returns {string}
                 */
                function (date) {
                    function pad (n) {
                        return n < 10 ? '0' + n : n;
                    }

                    date = (!isDate(date)) ? new Date(date) : date;
                    if (date.toString() === 'Invalid Date') {
                        return 'NaN';
                    }
                    return date.getUTCFullYear() +
                        '-' + pad(date.getUTCMonth() + 1) +
                        '-' + pad(date.getUTCDate()) +
                        ' ' + pad(date.getUTCHours()) +
                        ':' + pad(date.getUTCMinutes()) +
                        ':' + pad(date.getUTCSeconds()) +
                        'Z';
                });
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, null, null, formatter);
        }
        else if (dateFormats.date_ddd_MMM_d_yyy.regex.test(format)) {
            // "ddd, MMM d, yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        }
        else if (dateFormats.date_dddd_MMMM_d_yyyy.regex.test(format)) {
            // "dddd, MMMM d, yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });
        }
        else if (dateFormats.date_M_d_yyyy.regex.test(format)) {
            // "M/d/yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: 'numeric',
                day: 'numeric',
                year: 'numeric'
            });
        }
        else if (dateFormats.date_MM_dd_yyyy.regex.test(format)) {
            //NOTE: this is before the check for 2 digit year as both would match.

            // "MM/dd/yyyy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });
        }
        else if (dateFormats.date_MM_dd_yy.regex.test(format)) {
            // "MM/dd/yy"
            control.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                lang: language,
                month: '2-digit',
                day: '2-digit',
                year: '2-digit'
            });
        }
        return control.TextFormatterObj != null;
    }

    //create textFormatting OBJ based on dataType...MD
    switch (dataType) {
        case PropertyDataType.Integer:
            if (attemptNumberFormats(formatString, controlObj)) {
                // successful, this will drop down and return the formatter object.
            }
            else if (attemptDateFormats(formatString, controlObj)) {
                // successful, this will drop down and return the formatter object.
            }
            else {
                // did not recognize the format string, but we need a formatted value; use default number format.
                controlObj.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.NumberFormat, {
                    lang: language,
                    style: 'decimal',
                    useGrouping: false,
                    minimumIntegerDigits: 1
                });
            }
            break;

        case PropertyDataType.Decimal:
            if (attemptNumberFormats(formatString, controlObj)) {
                // successful, this will drop down and return the formatter object.
            }
            else if (attemptDateFormats(formatString, controlObj)) {
                // successful, this will drop down and return the formatter object.
            }
            else {
                // did not recognize the format string, but we need a formatted value; use default decimal format.
                controlObj.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.NumberFormat, {
                    lang: language,
                    style: 'decimal',
                    useGrouping: false,
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                    minimumIntegerDigits: 1
                });
            }
            break;

        case PropertyDataType.DateTime:
        case PropertyDataType.StaticDateTime:
            if (formatString == null || formatString === "") {
                // "M/d/yyyy h:mm:ss tt"

                /**
                 * if there is no format, we create a default formatting to prevent using .toString()...WR
                 * @type {ColumnTextFormatter}
                 */
                controlObj.TextFormatterObj = new ColumnTextFormatter(ColumnTextFormatter.DateTimeFormat, {
                    lang: language,
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                }, (formatted) => {
                    return formatted.replace(/,/g, "");
                });
            }
            else if (attemptDateFormats(formatString, controlObj)) {
                // successful, this will drop down and return the formatter object.
            }
            else {
                // We have a custom format, we need to create a custom formatter object and use the .NET formatter to handle this...KB
                controlObj.TextFormatterObj = new ColumnTextFormatter(FormatType.DateTimeFormat, null, null,
                    new FormatterObj(
                        /**
                         * Attempt use jsDate string formatting given the NET to JQuery date formatter string.
                         * @param {Date} value - The date value to format
                         * @returns {string}
                         */
                        function (value) {
                            if (value != null && value.toString != null) {
                                // attempt to use the jsDate library's override of "toString" to apply the format.
                                return value.toString(this._formatString);
                            }
                            else {
                                return "";
                            }
                        },
                        /**
                         * This is the .NET version of the format, we are caching this on the object to improve performance...KB
                         * @type {string}
                         */
                        NETtoJQueryFormatterDate(String(formatString).replace("{0:", "").replace("}", "")),
                    )
                );
            }
            break;

        case null:
            // We need to attempt to re-create the text formatter once we have data (since we have no data type).
            controlObj.TextFormatterObj = new ColumnTextFormatter(null, null, null,
                new FormatterObj(null, formatString));
            break;

        default:
            // no formatter available for this data type.
            controlObj.TextFormatterObj = null;
            break;
    }

    return controlObj.TextFormatterObj;
}
